// Pure formula evaluator, e.g. "=SUM(A1:C3)" → ok / empty / error.
// SUM/AVERAGE/COUNT; rows not in `rowOrder` contribute nothing.
// ERROR = parse failure or AVERAGE with no sources; REF = out-of-bounds / self-ref / cycle / oversize.

import type { ITableCell } from "../types";
import { type Coord, expandRange, rangeCellCount } from "./coord-translation";
import { extractCellText } from "./extract-cell-text";
import { parseFormula } from "./parse-formula";
import { type INumberAffix, parseSortableValue } from "./parse-sortable-value";

export type CalculationKind = "ok" | "empty" | "error";
export type CalculationErrorCode = "ERROR" | "REF";

export type CalculationResult =
  | { kind: "ok"; value: number; affix?: INumberAffix }
  | { kind: "empty" } // render 0
  // `cyclic` marks a REF caused by a reference cycle/depth-cap so it propagates
  // up as #REF; other errored contributors are skipped (see the loops below).
  | { kind: "error"; code: CalculationErrorCode; cyclic?: boolean };

export interface EvalContext {
  /** Flat row-major table cells. */
  tableData: ITableCell[];
  tableCols: number;
  tableRows: number;
  /** Visible row indices (post-sort/filter); null/undefined = every row contributes. */
  rowOrder?: number[] | null;
  /** Coord of the cell being evaluated; used for self-reference detection. */
  cellCoord: Coord;
  /** Coords being evaluated higher in the call stack (cloned on recursion). */
  visited?: Set<string>;
  /** Recursion depth (capped at MAX_DEPTH). */
  depth?: number;
}

const MAX_DEPTH = 8;
export const MAX_FORMULA_RANGE_CELLS = 10000;

const coordKey = (c: Coord): string => `${c.col},${c.row}`;

// Unanimity compares trimmed affixes so "$100" and "$ 100" agree; the result
// displays the first contributor's literal form, spacing included.
const sameAffix = (
  a: INumberAffix | undefined,
  b: INumberAffix | undefined,
): boolean => {
  if (!a || !b) return !a && !b;
  return a.prefix.trim() === b.prefix.trim() && a.suffix.trim() === b.suffix.trim();
};

const cellAt = (
  ctx: EvalContext,
  c: number,
  r: number,
): ITableCell | undefined => {
  if (c < 0 || c >= ctx.tableCols || r < 0 || r >= ctx.tableRows) return undefined;
  return ctx.tableData[r * ctx.tableCols + c];
};

const cellNumericValue = (
  cell: ITableCell | undefined,
  ctx: EvalContext,
  here: Coord,
): { ok: boolean; value: number; cyclic: boolean; affix?: INumberAffix } => {
  if (!cell || cell.mergedInto || cell.embedded) {
    return { ok: false, value: 0, cyclic: false };
  }
  if (cell.calculation) {
    if ((ctx.depth ?? 0) >= MAX_DEPTH || ctx.visited?.has(coordKey(here))) {
      return { ok: false, value: 0, cyclic: true };
    }
    const nestedVisited = new Set(ctx.visited ?? []);
    nestedVisited.add(coordKey(ctx.cellCoord));
    const nested = evaluateCalculation(cell.calculation.formula, {
      ...ctx,
      cellCoord: here,
      visited: nestedVisited,
      depth: (ctx.depth ?? 0) + 1,
    });
    if (nested.kind === "ok") {
      return { ok: true, value: nested.value, cyclic: false, affix: nested.affix };
    }
    // Only a cycle-caused error poisons the aggregate (→ #REF); a plain errored
    // contributor (e.g. AVERAGE of no data) is skipped, matching the loops.
    if (nested.kind === "error" && nested.cyclic) {
      return { ok: false, value: 0, cyclic: true };
    }
    return { ok: false, value: 0, cyclic: false };
  }
  const text = extractCellText(cell.content ?? "");
  if (!text) return { ok: false, value: 0, cyclic: false };
  const parsed = parseSortableValue(text);
  if (parsed.kind === "number" && typeof parsed.value === "number") {
    return { ok: true, value: parsed.value, cyclic: false, affix: parsed.affix };
  }
  return { ok: false, value: 0, cyclic: false };
};

/** Evaluate a formula string against the table. Pure. */
export const evaluateCalculation = (
  formula: string,
  ctx: EvalContext,
): CalculationResult => {
  const parsed = parseFormula(formula);
  if (!parsed.ok) return { kind: "error", code: "ERROR" };
  const { func, refs } = parsed;

  // Any single offending ref poisons the whole formula.
  const inBounds = (c: Coord): boolean =>
    c.col >= 0 && c.col < ctx.tableCols && c.row >= 0 && c.row < ctx.tableRows;

  let totalCells = 0;
  for (const r of refs) {
    totalCells += rangeCellCount(r.start, r.end);
    if (totalCells > MAX_FORMULA_RANGE_CELLS) {
      return { kind: "error", code: "REF" };
    }
    if (!inBounds(r.start) || !inBounds(r.end)) {
      return { kind: "error", code: "REF" };
    }
    const containsSelf =
      ctx.cellCoord.col >= Math.min(r.start.col, r.end.col) &&
      ctx.cellCoord.col <= Math.max(r.start.col, r.end.col) &&
      ctx.cellCoord.row >= Math.min(r.start.row, r.end.row) &&
      ctx.cellCoord.row <= Math.max(r.start.row, r.end.row);
    if (containsSelf) return { kind: "error", code: "REF" };
  }

  const visibleRows = ctx.rowOrder ? new Set(ctx.rowOrder) : null;

  // Dedupe coords across refs so `=SUM(A1,A1)` doesn't double-count.
  const seen = new Set<string>();
  const coords: Coord[] = [];
  for (const r of refs) {
    for (const c of expandRange(r.start, r.end)) {
      const key = `${c.col},${c.row}`;
      if (seen.has(key)) continue;
      seen.add(key);
      coords.push(c);
    }
  }

  // A plain errored contributor does not poison the aggregate (SUM 0, COUNT
  // skip, AVERAGE excludes), but a reference CYCLE surfaces as #REF.
  if (func === "COUNT") {
    let count = 0;
    let cyclic = false;
    for (const c of coords) {
      if (visibleRows && !visibleRows.has(c.row)) continue;
      const r = cellNumericValue(cellAt(ctx, c.col, c.row), ctx, c);
      if (r.cyclic) cyclic = true;
      if (r.ok) count++;
    }
    if (cyclic) return { kind: "error", code: "REF", cyclic: true };
    return { kind: "ok", value: count };
  }

  let sum = 0;
  let n = 0;
  let cyclic = false;
  // Only contributing cells vote; a single disagreement drops the affix, since
  // a total mixing dollars and euros must not claim to be either.
  let affix: INumberAffix | undefined;
  let affixSeen = false;
  let affixUnanimous = true;
  for (const c of coords) {
    if (visibleRows && !visibleRows.has(c.row)) continue;
    const r = cellNumericValue(cellAt(ctx, c.col, c.row), ctx, c);
    if (r.cyclic) cyclic = true;
    if (r.ok) {
      sum += r.value;
      n++;
      if (!affixSeen) {
        affix = r.affix;
        affixSeen = true;
      } else if (!sameAffix(affix, r.affix)) {
        affixUnanimous = false;
      }
    }
  }
  if (cyclic) return { kind: "error", code: "REF", cyclic: true };

  const resultAffix = affixUnanimous ? affix : undefined;

  if (func === "AVERAGE") {
    if (n === 0) return { kind: "error", code: "ERROR" };
    const value = sum / n;
    return resultAffix ? { kind: "ok", value, affix: resultAffix } : { kind: "ok", value };
  }
  return resultAffix ? { kind: "ok", value: sum, affix: resultAffix } : { kind: "ok", value: sum };
};

/** Format an evaluation result to the string the renderer prints. Floats cap at 2 decimals. */
export const formatCalculationResult = (result: CalculationResult): string => {
  if (result.kind === "error") return `#${result.code}`;
  if (result.kind === "empty") return "0";
  const { affix } = result;
  // The parser stores percents as fractions; only display scales back up.
  const isPercent = affix?.suffix.trim() === "%";
  const v = isPercent ? result.value * 100 : result.value;
  const body = Number.isInteger(v)
    ? v.toLocaleString()
    : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return affix ? `${affix.prefix}${body}${affix.suffix}` : body;
};

/**
 * Locale-independent numeric string for sort/filter KEYS (no grouping
 * separators). Must not go through toLocaleString — grouped/localized output
 * breaks numeric comparison and round-tripping via parseSortableValue.
 */
export const rawCalculationResult = (result: CalculationResult): string => {
  if (result.kind === "error") return `#${result.code}`;
  if (result.kind === "empty") return "0";
  const v = result.value;
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};
